import * as angular from "angular";
import * as _ from 'lodash';
import {
  IDeferred,
  IHttpService,
  ILogService,
  IPromise,
  IQService,
  IRequestConfig,
  IRequestShortcutConfig
} from "angular";
import {ApiConfig} from "../models/api-config.model";

export class HttpService {

  constructor(private $http: IHttpService,
              private $q: IQService,
              private apiConfig: ApiConfig,
              private $log: ILogService) {
  }

  /**
   * Asynchronous HTTP calls handler - generic for all 'GET' calls.
   *
   * @summary 'GET' handler
   *
   * @param {string} getUrl - the url path.
   * @param {object} config - $http request configuration object.
   *
   * @return {promise} deferred - resolved to a response object when the request succeeds or fails.
   * **/
  private get(getUrl: string, config?: IRequestShortcutConfig) {
    return this.$http.get(this.apiConfig.baseUrl + getUrl, config);
  }

  /**
   * Asynchronous HTTP calls handler - generic for all 'POST' calls.
   *
   * @summary 'POST' handler
   *
   * @param {string} postUrl - the url path.
   * @param {object} data - payload.
   * @param {object} config - $http request configuration object.
   *
   * @return {promise} deferred - resolved to a response object when the request succeeds or fails.
   * **/
  private post(postUrl: string, data: any, config?: IRequestShortcutConfig) {
    return this.$http.post(this.apiConfig.baseUrl + postUrl, data, config);
  }

  /**
   * Asynchronous HTTP calls handler - generic for all 'PUT' calls.
   *
   * @summary 'PUT' handler
   *
   * @param {string} putUrl - the url path.
   * @param {object} data - payload.
   * @param {object} config - $http request configuration object.
   *
   * @return {promise} deferred - resolved to a response object when the request succeeds or fails.
   * **/
  private put(putUrl: string, data: any, config?: IRequestShortcutConfig) {
    return this.$http.put(this.apiConfig.baseUrl + putUrl, data, config);
  }

  /**
   * Asynchronous HTTP calls handler - generic for all 'DELETE' calls.
   *
   * @summary 'DELETE' handler
   *
   * @param {string} deleteUrl - the url path
   * @param {object} config - $http request configuration object.
   *
   * @return {promise} deferred - resolved to a response object when the request succeeds or fails.
   *
   * **/
  private asyncDelete(deleteUrl: string, config?: IRequestShortcutConfig) {
    return this.$http.delete(deleteUrl, config);
  }

  /**
   * Accepts the base url for the API and the corresponding parameters to create an API path including all of the
   * path params using regular expression.
   *
   * @summary - generate API path
   *
   * @param {string} url
   * @param {object} options - key/value for API path parameters
   *
   * @return {string} url - the generated API path
   * **/
  private urlBuilder(url: string, options: { [key: string]: string }): string {

    let finalUrl: string = url;

    const flags = 'ig';

    _.forEach(options, (value, key) => {

      const pattern = new RegExp('{' + key + '}', flags);
      const replaceWith = '' + encodeURIComponent(value);

      finalUrl = finalUrl.replace(pattern, replaceWith);
    });

    return finalUrl;
  }

  private createRequest(url: string, config: IRequestConfig, options?: { [key: string]: string }): IRequestConfig {

    let finalUrl: string = _.isUndefined(options) || _.isNull(options) ? url : this.urlBuilder(url, options);

    let request: IRequestConfig = {
      url: this.apiConfig.baseUrl + finalUrl,
      method: config.method,
      headers: config.headers,
      data: config.data,
      params: config.params
    };

    // FIXME: Why we need that?
    // Note: Don't remove empty 'data' - backend may expect empty body for POST/PUT requests
    return _.forOwn(request, (value, key, object) => {
      if (_.isUndefined(value) || (_.isEmpty(value) && key !== 'data')) {
        _.unset(object, key);
      }
    });

  }

  request<T>(url: string, config: IRequestConfig, options?: { [key: string]: string }): IPromise<T> {

    let deferred: IDeferred<T> = this.$q.defer<T>();
    let overriddenConfig: IRequestConfig = this.createRequest(url, config, options);

    this.$http<T>(overriddenConfig).then((response) => {
        deferred.resolve(response.data);
      },
      (err) => {
        this.$log.error('[HttpService] Error during HTTP request: ', err);
        deferred.reject(err);
      }
    );

    return deferred.promise;
  }
}

angular
  .module('bringoz.admin.commons')
  .service('httpService', HttpService);
